import { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Server, ArrowRight, Clock, DollarSign, Shield, Users, AlertTriangle,
  CheckCircle2, ChevronRight, BarChart3, Layers, Zap, Building2,
  Globe, FileText, ChevronDown, Minus, Plus, Lock, Download,
  Target, Cpu, Headphones, Eye, ArrowUpRight, BookOpen, X, Loader2, Mail
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import SapSubNav from '@/components/sap/SapSubNav';
import SapGameChallenge from '@/components/sap/SapGameChallenge';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════ */

const STATS = [
  { value: '37,000+', label: 'Companies migrating to S/4HANA globally' },
  { value: 'Dec 2027', label: 'SAP ECC end of mainstream support' },
  { value: '60%', label: 'Of migrations experience compliance delays' },
  { value: '40-60%', label: 'TCO reduction with platform consolidation' },
];

const TIMELINE = [
  { year: '2025', event: 'SAP ECC extended maintenance ends for many customers', status: 'now' },
  { year: '2026', event: 'Peak migration wave — majority of enterprises in active S/4HANA projects', status: 'now' },
  { year: '2027', event: 'SAP ECC mainstream support officially ends December 31', status: 'upcoming' },
  { year: '2028+', event: 'Custom maintenance only — significantly higher cost, no enhancements', status: 'future' },
];

const PROGRAM_PHASES = [
  { phase: 'Discover', desc: 'Assess current landscape, define scope', taxera: 'Compliance audit across all countries. Map current vendors, gaps, and risks.', color: '#0066CC' },
  { phase: 'Prepare', desc: 'Design target architecture, plan migration', taxera: 'Platform configuration. Mandate coverage mapped. Integration design with S/4 data model.', color: '#14b8a6' },
  { phase: 'Explore', desc: 'Configure S/4, build integrations', taxera: 'Middleware deployment inside SAP BTP. Tax determination + reporting connected.', color: '#8b5cf6' },
  { phase: 'Realize', desc: 'Test, validate, prepare for go-live', taxera: 'End-to-end compliance testing. Parallel run with live mandates. User acceptance.', color: '#f59e0b' },
  { phase: 'Deploy', desc: 'Go-live and hypercare', taxera: 'Cutover support. Real-time monitoring. Compliance continuity guaranteed.', color: '#10b981' },
  { phase: 'Run', desc: 'Operate, optimize, evolve', taxera: 'Ongoing mandate updates absorbed automatically. New countries onboarded. Continuous improvement.', color: '#64748b' },
];

const MARKET_APPROACHES = [
  {
    approach: 'US-Centric Tax Engine + Bolt-Ons',
    traits: ['Strong in North American tax determination', 'Global compliance via acquired or partnered add-ons', 'Multiple platforms, multiple contracts', 'Integration complexity increases with each add-on'],
    limitation: 'Designed for US tax calculation first. Global compliance is an afterthought, bolted on through acquisitions and partnerships — not a unified platform.',
  },
  {
    approach: 'Legacy Software Amalgamation',
    traits: ['Built through years of acquisitions', 'Multiple legacy codebases stitched together', 'Broad coverage on paper, fragmented in practice', 'Inconsistent UX across modules'],
    limitation: 'A collection of acquired products, not a platform. Each module has different architecture, different support teams, different roadmaps.',
  },
  {
    approach: 'Legacy Platform + Network Acquisition',
    traits: ['Established brand with deep market presence', 'Recent acquisition of e-Invoicing network', 'Integration of acquired technology still in progress', 'Complex licensing models'],
    limitation: 'Merging a legacy compliance platform with an acquired network creates integration debt. Customers bear the cost of convergence.',
  },
  {
    approach: 'ERP-Native but Limited',
    traits: ['Built into the ERP ecosystem', 'Basic document compliance capabilities', 'Limited mandate coverage and depth', 'No dedicated compliance expertise'],
    limitation: 'Covers the basics but lacks the depth, coverage, and specialization that complex multinationals require. You outgrow it fast.',
  },
];

const TAXERA_DIFFERENTIATORS = [
  { label: 'Single global platform', desc: 'One codebase, one UX, one contract — not stitched-together acquisitions', icon: Globe },
  { label: 'Cloud-native on SAP BTP', desc: 'Born in the cloud, not migrated. No legacy architecture debt', icon: Cpu },
  { label: 'AI-powered intelligence', desc: 'Regulatory scanning, mandate mapping, and anomaly detection built in', icon: Zap },
  { label: 'Speed to value', desc: '8-12 week implementation. Parallel with your S/4 project, not extending it', icon: Clock },
  { label: 'Simple licensing', desc: 'Transparent per-country pricing. No hidden fees, no surprise true-ups', icon: DollarSign },
  { label: 'Platform + PM included', desc: 'Dedicated program management bundled. Your migration, our shared responsibility', icon: Users },
  { label: 'Exceptional UX', desc: 'Designed for tax professionals, not developers. Intuitive from day one', icon: Eye },
  { label: 'White-glove support', desc: 'Named support contacts. Not a ticket queue — a partnership', icon: Headphones },
];

const ASSESSMENT_QUESTIONS = [
  { id: 'cloud', dimension: 'Cloud Strategy', question: 'Where is your organization in the SAP cloud journey?', options: [
    { label: 'Still on ECC with no migration timeline', score: 1 },
    { label: 'Planning phase — evaluating S/4HANA options', score: 2 },
    { label: 'Active migration project underway', score: 3 },
    { label: 'Already on S/4HANA (Cloud or On-Premise)', score: 4 },
  ]},
  { id: 'coverage', dimension: 'Geographic Coverage', question: 'In how many countries does your organization need tax compliance?', options: [
    { label: '1-5 countries', score: 4 },
    { label: '6-15 countries', score: 3 },
    { label: '16-30 countries', score: 2 },
    { label: '30+ countries', score: 1 },
  ]},
  { id: 'vendors', dimension: 'Vendor Consolidation', question: 'How many separate compliance vendors or solutions do you currently manage?', options: [
    { label: '1 global platform', score: 4 },
    { label: '2-3 regional solutions', score: 3 },
    { label: '4-7 country-specific vendors', score: 2 },
    { label: '8+ point solutions and manual processes', score: 1 },
  ]},
  { id: 'integration', dimension: 'Integration Depth', question: 'How deeply integrated is your compliance technology with your ERP?', options: [
    { label: 'Native middleware inside SAP business process layer', score: 4 },
    { label: 'API-connected external platform', score: 3 },
    { label: 'File-based data exchange (batch)', score: 2 },
    { label: 'Manual data extraction and upload', score: 1 },
  ]},
  { id: 'automation', dimension: 'Automation Level', question: 'What percentage of your compliance processes run end-to-end without manual intervention?', options: [
    { label: '80%+ fully automated', score: 4 },
    { label: '50-80% automated with some manual steps', score: 3 },
    { label: '20-50% automated, significant manual work', score: 2 },
    { label: 'Less than 20% — mostly manual', score: 1 },
  ]},
  { id: 'readiness', dimension: 'Mandate Readiness', question: 'How prepared is your organization for new regulatory mandates in the next 12 months?', options: [
    { label: 'Fully prepared — our platform absorbs new mandates automatically', score: 4 },
    { label: 'Mostly prepared — minor effort per new mandate', score: 3 },
    { label: 'Partially — each new mandate requires a separate project', score: 2 },
    { label: 'Not prepared — reactive approach to every change', score: 1 },
  ]},
  { id: 'support', dimension: 'Support & Expertise', question: 'How would you rate your current compliance support experience?', options: [
    { label: 'Excellent — proactive, responsive, deeply knowledgeable', score: 4 },
    { label: 'Good — generally responsive and helpful', score: 3 },
    { label: 'Adequate — meets minimum requirements but slow', score: 2 },
    { label: 'Poor — fragmented across vendors, no accountability', score: 1 },
  ]},
  { id: 'cost', dimension: 'Cost Visibility', question: 'Do you have full visibility into your total compliance cost of ownership?', options: [
    { label: 'Yes — single invoice, fully predictable', score: 4 },
    { label: 'Mostly — costs tracked but spread across budgets', score: 3 },
    { label: 'Partially — some hidden costs and variable fees', score: 2 },
    { label: 'No — fragmented, unpredictable, and growing each year', score: 1 },
  ]},
];

const USE_CASES = [
  {
    industry: 'Global Pharmaceutical',
    challenge: 'Operating in 42 countries with 11 separate compliance vendors. S/4HANA migration created an opportunity to consolidate but timeline pressure was extreme.',
    outcome: 'Deployed Taxera across all 42 countries in parallel with S/4 migration. Eliminated 11 vendor contracts. Achieved compliance continuity with zero filing disruptions during cutover.',
    metrics: [{ label: 'Countries', value: '42' }, { label: 'Vendors replaced', value: '11' }, { label: 'Filing disruptions', value: '0' }],
  },
  {
    industry: 'European Automotive OEM',
    challenge: 'Mid-migration to S/4HANA when France and Poland announced new mandatory e-Invoicing deadlines. Existing compliance setup had no path to support these mandates on the new platform.',
    outcome: 'Taxera deployed on SAP BTP within 10 weeks. France e-Invoicing and Poland KSeF configured and tested before S/4 go-live. Tax team went from reactive to proactive.',
    metrics: [{ label: 'Implementation', value: '10 weeks' }, { label: 'New mandates covered', value: '2' }, { label: 'Go-live delay', value: 'None' }],
  },
  {
    industry: 'Fortune 500 Consumer Goods',
    challenge: 'Legacy compliance infrastructure built over 15 years — mix of local vendors, custom ABAP, and manual processes. Total compliance spend unknown across 28 countries.',
    outcome: 'Full platform consolidation onto Taxera. First time the CFO had a single view of compliance cost and status across all markets. Annual TCO reduced by 52%.',
    metrics: [{ label: 'Countries consolidated', value: '28' }, { label: 'TCO reduction', value: '52%' }, { label: 'Visibility', value: 'Real-time' }],
  },
];

const BLOG_REFS = [
  { slug: 'tax-technology-revolution-2026', title: 'The Tax Technology Revolution: Why 2026 Is the Tipping Point' },
  { slug: 'reactive-to-proactive-tax-function', title: 'From Reactive to Proactive: Building a Future-Ready Tax Function' },
  { slug: 'hidden-cost-of-doing-nothing', title: 'The Real Cost of Doing Nothing' },
  { slug: 'erp-integration-middleware-vs-api', title: 'ERP Integration Deep-Dive: Middleware vs API' },
];

const PERSONAS = [
  {
    role: 'Head of Tax / VP Tax',
    color: '#0066CC',
    icon: Shield,
    concerns: [
      'Will our compliance processes survive the migration without a single filing disruption?',
      'Can we use S/4 as the catalyst to consolidate our fragmented vendor landscape?',
      'How do we maintain regulatory readiness for France, Poland, and Belgium mandates during the transition?',
    ],
    answer: 'Taxera ensures zero compliance disruption during your S/4 migration. Our platform handles all mandates across all countries on a single stack — eliminating vendor fragmentation and providing a compliance safety net during the most critical phase of your transformation. New mandates are absorbed automatically.',
  },
  {
    role: 'CIO / VP IT',
    color: '#8b5cf6',
    icon: Server,
    concerns: [
      'How many additional integration points does this add to our already complex S/4 landscape?',
      'Will this run natively on our SAP BTP infrastructure or require separate hosting?',
      'What is the implementation timeline relative to our S/4 go-live?',
    ],
    answer: 'Taxera is SAP BTP-native. One integration point, zero additional infrastructure. Our middleware operates inside your SAP business process layer — data never leaves the ecosystem. Standard implementation: 8-12 weeks, designed to run in parallel with your S/4 project. Your architecture stays clean.',
  },
  {
    role: 'CFO / Finance Director',
    color: '#14b8a6',
    icon: DollarSign,
    concerns: [
      'What is the TCO impact of addressing compliance during migration versus retrofitting after go-live?',
      'How does platform consolidation affect our total compliance spend trajectory?',
      'What is the penalty exposure if compliance breaks during migration?',
    ],
    answer: 'Addressing compliance during migration reduces TCO by 40-60% compared to retrofitting. Platform consolidation typically eliminates 3-7 vendor contracts. And the cost of a compliance failure — penalties, audit exposure, reputational risk — far exceeds the investment. Simple, predictable licensing with no hidden fees.',
  },
  {
    role: 'Program Manager / PMO',
    color: '#f59e0b',
    icon: Target,
    concerns: [
      'Will adding compliance scope extend our migration timeline?',
      'How do we manage dependencies between the S/4 workstream and compliance workstream?',
      'What resources do we need from our side?',
    ],
    answer: 'Taxera deploys in parallel with your S/4 migration — not on the critical path. We include dedicated program management in every engagement. Dependencies are minimal: we need your chart of accounts and master data mapping. Our team handles the rest. Your milestone dates stay intact.',
  },
];

/* ═══════════════════════════════════════════════
   RADAR CHART (SVG)
   ═══════════════════════════════════════════════ */
const RadarChart = ({ scores, dimensions }) => {
  const n = dimensions.length;
  const cx = 140, cy = 140, maxR = 110;
  const levels = [1, 2, 3, 4];

  const getPoint = (i, r) => {
    const angle = (i * 2 * Math.PI) / n - Math.PI / 2;
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  };

  const gridPolygons = levels.map(level => {
    const r = (level / 4) * maxR;
    return Array.from({ length: n }, (_, i) => getPoint(i, r)).map(p => `${p.x},${p.y}`).join(' ');
  });

  const dataPoints = scores.map((s, i) => getPoint(i, (s / 4) * maxR));
  const dataPolygon = dataPoints.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <svg viewBox="0 0 280 280" className="w-full max-w-[300px] mx-auto">
      {/* Grid */}
      {gridPolygons.map((pts, i) => (
        <polygon key={`poly-${i}`} points={pts} fill="none" stroke="#e2e8f0" strokeWidth={i === 3 ? 1.5 : 0.5} />
      ))}
      {/* Axes */}
      {Array.from({ length: n }, (_, i) => {
        const p = getPoint(i, maxR);
        return <line key={`line-${i}`} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#e2e8f0" strokeWidth={0.5} />;
      })}
      {/* Data polygon */}
      <polygon points={dataPolygon} fill="rgba(20,184,166,0.15)" stroke="#14b8a6" strokeWidth={2} />
      {dataPoints.map((p, i) => (
        <circle key={`dot-${i}`} cx={p.x} cy={p.y} r={4} fill="#14b8a6" />
      ))}
      {/* Labels */}
      {dimensions.map((dim, i) => {
        const p = getPoint(i, maxR + 22);
        return (
          <text key={`label-${i}`} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="middle" className="text-2xs fill-slate-500 font-semibold">
            {dim.length > 12 ? dim.slice(0, 11) + '.' : dim}
          </text>
        );
      })}
    </svg>
  );
};

/* ═══════════════════════════════════════════════
   LEAD GATE MODAL
   ═══════════════════════════════════════════════ */
const LeadGateModal = ({ open, onClose, onSuccess, title, subtitle, source }) => {
  const [form, setForm] = useState({ name: '', email: '', company: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) { setError('All fields are required'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/leads/sap-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source, resource_type: source }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Something went wrong');
        return;
      }
      onSuccess(form);
    } catch { setError('Network error. Please try again.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="lead-gate-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400" data-testid="lead-gate-close"><X className="w-4 h-4" /></button>
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-2xl bg-taxera-teal/10 flex items-center justify-center mx-auto mb-3"><Download className="w-5 h-5 text-taxera-teal" /></div>
          <h3 className="font-heading text-lg font-bold text-taxera-charcoal">{title}</h3>
          <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input type="text" placeholder="Full name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" data-testid="lead-name-input" />
          <input type="email" placeholder="Work email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" data-testid="lead-email-input" />
          <input type="text" placeholder="Company" value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" data-testid="lead-company-input" />
          {error && <p className="text-xs text-red-500">{error}</p>}
          <button type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 taxera-gradient text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50" data-testid="lead-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {saving ? 'Submitting...' : 'Get Your Report'}
          </button>
          <p className="text-2xs text-slate-400 text-center">We respect your privacy. No spam, ever.</p>
        </form>
      </motion.div>
    </div>
  );
};

/* ─── Print-friendly report generator ─── */
const generateAssessmentReport = (scores, overallPct, grade, userName) => {
  const dimensionRows = ASSESSMENT_QUESTIONS.map((q, i) => {
    const score = scores[i];
    const barWidth = (score / 4) * 100;
    const barColor = score >= 3 ? '#10b981' : score >= 2 ? '#f59e0b' : '#ef4444';
    return `<tr><td style="padding:8px 12px;font-size:13px;color:#475569;border-bottom:1px solid #f1f5f9">${q.dimension}</td><td style="padding:8px 12px;border-bottom:1px solid #f1f5f9"><div style="background:#f1f5f9;border-radius:999px;height:8px;width:100%"><div style="background:${barColor};border-radius:999px;height:8px;width:${barWidth}%"></div></div></td><td style="padding:8px 12px;font-size:13px;font-weight:700;color:#475569;text-align:right;border-bottom:1px solid #f1f5f9">${score}/4</td></tr>`;
  }).join('');

  const html = `<!DOCTYPE html><html><head><title>Taxera SAP S/4HANA Migration Readiness Report</title><style>@media print{body{margin:0}}.page{max-width:700px;margin:40px auto;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif}h1{font-size:28px;color:#1e293b}h2{font-size:18px;color:#1e293b;margin-top:32px;border-bottom:2px solid #14b8a6;padding-bottom:8px}.score-box{background:linear-gradient(135deg,#0066CC,#14b8a6);border-radius:16px;padding:32px;text-align:center;color:#fff;margin:24px 0}.score-num{font-size:64px;font-weight:800}.score-label{font-size:20px;font-weight:700;margin-top:4px}table{width:100%;border-collapse:collapse;margin-top:12px}.reco{background:#f8fafc;border-radius:12px;padding:20px;margin-top:16px;border-left:4px solid #14b8a6}.footer{margin-top:40px;padding-top:16px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;text-align:center}</style></head><body><div class="page"><div style="display:flex;align-items:center;justify-content:between;margin-bottom:8px"><div><span style="font-size:24px;font-weight:800;color:#1e293b">taxera</span><span style="font-size:11px;color:#94a3b8;margin-left:12px">SAP S/4HANA Migration Readiness Assessment</span></div></div><hr style="border:none;border-top:1px solid #e2e8f0;margin:16px 0"><p style="font-size:13px;color:#64748b">Prepared for: <strong>${userName || 'Assessment Participant'}</strong> | ${new Date().toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}</p><div class="score-box"><div class="score-num">${overallPct}%</div><div class="score-label">${grade.label}</div></div><p style="font-size:14px;color:#475569;line-height:1.6">${grade.desc}</p><h2>Dimension Scores</h2><table>${dimensionRows}</table><h2>Recommendations</h2><div class="reco"><p style="font-size:13px;color:#475569;line-height:1.6;margin:0">${overallPct >= 60 ? 'Your organization has a solid foundation. To maximize your S/4 migration success, consider consolidating your compliance vendors onto a single platform and automating manual processes before go-live. Taxera can help you close remaining gaps in coverage and integration depth.' : 'Your compliance infrastructure needs significant attention before or during your S/4HANA migration. We strongly recommend a compliance readiness workshop with Taxera to map gaps, quantify risk, and build a remediation roadmap. Every week of delay increases the cost and complexity of transformation.'}</p></div><h2>About This Assessment</h2><p style="font-size:13px;color:#475569;line-height:1.6">This assessment evaluates your organization across 8 critical dimensions of compliance readiness for SAP S/4HANA migration. Scores range from 1 (Critical) to 4 (Excellent) per dimension.</p><h2>Next Steps</h2><p style="font-size:13px;color:#475569;line-height:1.6">Schedule a complimentary 30-minute consultation with a Taxera SAP compliance specialist to discuss your results and explore how we can support your migration journey.</p><p style="margin-top:12px"><strong style="color:#14b8a6">Contact:</strong> <a href="mailto:info@taxera.tech" style="color:#0066CC">info@taxera.tech</a> | <a href="https://taxera.tech" style="color:#0066CC">taxera.tech</a></p><div class="footer">Taxera Technologies GmbH | This report is confidential and intended for the named recipient only.</div></div></body></html>`;

  const win = window.open('', '_blank');
  if (win) { const blob = new Blob([html], { type: 'text/html' }); win.location.href = URL.createObjectURL(blob); setTimeout(() => win.print(), 500); }
};

/* ─── Whitepaper Download Button (standalone, reusable) ─── */
const WhitepaperDownloadBtn = () => {
  const [showGate, setShowGate] = useState(false);
  const [captured, setCaptured] = useState(false);

  const handleSuccess = (form) => {
    setCaptured(true);
    setShowGate(false);
    generateWhitepaper(form.name);
  };

  return (
    <>
      <LeadGateModal
        open={showGate}
        onClose={() => setShowGate(false)}
        onSuccess={handleSuccess}
        title="Download Whitepaper"
        subtitle="Get the complete SAP S/4HANA tax compliance guide."
        source="whitepaper"
      />
      <button
        onClick={() => captured ? generateWhitepaper('') : setShowGate(true)}
        className="group inline-flex items-center gap-2 px-7 py-3.5 bg-taxera-teal text-white rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105"
        data-testid="whitepaper-download-btn"
      >
        <Download className="w-4 h-4" /> Download Whitepaper
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </button>
    </>
  );
};

/* ─── Whitepaper content generator ─── */
const generateWhitepaper = (userName) => {
  const html = `<!DOCTYPE html><html><head><title>Taxera Whitepaper: Tax Compliance in the Age of SAP S/4HANA</title><style>@media print{body{margin:0}}.page{max-width:700px;margin:40px auto;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#334155;line-height:1.7}h1{font-size:32px;color:#1e293b;line-height:1.2}h2{font-size:20px;color:#1e293b;margin-top:36px;padding-bottom:8px;border-bottom:2px solid #14b8a6}h3{font-size:16px;color:#1e293b;margin-top:24px}p{font-size:14px;margin-top:12px}.cover{background:linear-gradient(135deg,#0f172a,#1e3a5f);border-radius:20px;padding:48px;color:#fff;margin-bottom:32px}.cover h1{color:#fff;font-size:36px}.cover p{color:#94a3b8;font-size:15px}.stat-row{display:flex;gap:16px;margin:24px 0}.stat-box{flex:1;background:#f8fafc;border-radius:12px;padding:16px;text-align:center;border:1px solid #e2e8f0}.stat-num{font-size:24px;font-weight:800;color:#0066CC}.stat-label{font-size:11px;color:#64748b;margin-top:4px}.callout{background:#f0fdfa;border-left:4px solid #14b8a6;padding:16px 20px;border-radius:0 12px 12px 0;margin:20px 0}ul{padding-left:20px}li{font-size:14px;margin-top:6px}.footer{margin-top:40px;padding-top:16px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;text-align:center}</style></head><body><div class="page">
<div class="cover"><p style="font-size:12px;text-transform:uppercase;letter-spacing:2px;color:#14b8a6;margin-bottom:16px">TAXERA WHITEPAPER</p><h1>Tax Compliance in the Age of SAP S/4HANA</h1><p style="margin-top:16px">A strategic guide for Tax Directors, CIOs, and CFOs navigating the intersection of ERP transformation and regulatory compliance.</p><p style="font-size:12px;color:#64748b;margin-top:24px">Prepared for: ${userName || 'Enterprise Leaders'} | ${new Date().toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}</p></div>

<h2>Executive Summary</h2><p>The migration from SAP ECC to S/4HANA represents the largest ERP transformation in enterprise history. With SAP ending mainstream ECC support in December 2027, over 37,000 organizations are executing or planning their transition. What many discover too late is that their tax compliance infrastructure — built over years of organic growth — does not survive the move intact.</p><p>This whitepaper examines why tax compliance is one of the most under-estimated workstreams in S/4HANA migrations, the risks of getting it wrong, and how a platform-based approach eliminates these risks while reducing total cost of ownership by 40-60%.</p>

<h2>The Scale of the Challenge</h2><div class="stat-row"><div class="stat-box"><div class="stat-num">37,000+</div><div class="stat-label">Companies migrating globally</div></div><div class="stat-box"><div class="stat-num">60%</div><div class="stat-label">Experience compliance delays</div></div><div class="stat-box"><div class="stat-num">Dec 2027</div><div class="stat-label">ECC end of support</div></div></div>
<p>For a multinational operating in 20+ countries, the compliance landscape during migration is extraordinarily complex. Tax configurations must be rebuilt. Data models change. Existing integrations break. And the regulatory environment continues to evolve — France, Poland, Belgium, and Germany are all introducing new mandatory requirements during the peak migration window.</p>

<h2>What Goes Wrong</h2><h3>1. Compliance Disruption During Migration</h3><p>Tax determination rules, reporting configurations, and compliance workflows built in ECC don't automatically carry forward to S/4HANA. Without careful planning, organizations face filing failures, rejected submissions, and regulatory penalties during the most vulnerable phase of their transformation.</p>
<h3>2. Data Mapping Gaps</h3><p>S/4HANA introduces a simplified data model with the Universal Journal (ACDOCA). Chart of accounts restructuring, new material types, and business partner changes all impact how tax data flows through the system. Legacy compliance solutions designed for ECC's data model break.</p>
<h3>3. Vendor Fragmentation Amplified</h3><p>Most multinationals have accumulated 5-10+ compliance vendors over the years — each covering specific countries or mandate types. Migration exposes the true cost of this fragmentation: each vendor needs a new integration, new testing, and new validation for S/4HANA.</p>
<h3>4. Timeline Overruns</h3><p>Tax compliance is routinely the last workstream addressed in S/4 projects. Late discovery of compliance gaps has been shown to extend go-live timelines by 3-6 months — at a cost of hundreds of thousands of euros per month in delayed value realization.</p>

<h2>The Market Landscape</h2><p>Organizations evaluating compliance solutions for their S/4 migration encounter four broad categories:</p>
<ul><li><strong>US-Centric Tax Engines + Bolt-Ons:</strong> Strong in North American tax determination, but global compliance is achieved through acquired or partnered add-ons. Multiple platforms, multiple contracts, increasing integration complexity.</li>
<li><strong>Legacy Software Amalgamations:</strong> Built through years of acquisitions. Multiple codebases stitched together. Broad coverage on paper, fragmented in practice with inconsistent UX and support.</li>
<li><strong>Legacy Platforms + Network Acquisitions:</strong> Established brands combining legacy compliance software with recently acquired e-Invoicing networks. Integration of technologies still in progress. Customers bear the cost of convergence.</li>
<li><strong>ERP-Native but Limited:</strong> Basic compliance capabilities built into the ERP ecosystem. Covers fundamentals but lacks the depth, coverage, and specialization complex multinationals require.</li></ul>

<div class="callout"><strong>The Taxera Approach:</strong> A born-cloud, single-platform solution built from the ground up for global compliance. Not acquired. Not bolted-on. One codebase, one UX, one contract — with native SAP BTP integration and middleware that operates inside your SAP business process layer.</div>

<h2>The Platform Advantage</h2><p>A unified compliance platform addresses every challenge of the S/4 migration:</p>
<ul><li><strong>Single integration point:</strong> One middleware connection to SAP, covering all mandates across all countries</li>
<li><strong>S/4 data model native:</strong> Designed for the Universal Journal and simplified data model from day one</li>
<li><strong>Parallel deployment:</strong> 8-12 week implementation that runs alongside your S/4 project — not extending it</li>
<li><strong>Automatic mandate absorption:</strong> New regulatory requirements handled by platform updates, not new projects</li>
<li><strong>Vendor consolidation:</strong> Eliminate 3-7 point solutions and their associated management overhead</li>
<li><strong>Real-time visibility:</strong> Single dashboard showing compliance status across all markets</li></ul>

<h2>Financial Impact</h2><p>Based on industry benchmarks for a multinational with 20 countries and 5 existing compliance vendors:</p>
<div class="stat-row"><div class="stat-box"><div class="stat-num">40-60%</div><div class="stat-label">TCO reduction</div></div><div class="stat-box"><div class="stat-num">3-7</div><div class="stat-label">Vendors eliminated</div></div><div class="stat-box"><div class="stat-num">12 mo</div><div class="stat-label">Typical payback</div></div></div>
<p>The cost of addressing compliance during migration is significantly lower than retrofitting after go-live. Organizations that defer compliance workstreams face 1.5-2x higher implementation costs and continued fragmentation risk.</p>

<h2>Recommendations</h2>
<ul><li>Include compliance as a Day 1 workstream in your S/4HANA program — not an afterthought</li>
<li>Conduct a compliance audit across all countries before architecture decisions are finalized</li>
<li>Evaluate platform consolidation as part of the business case for S/4 — the savings offset migration investment</li>
<li>Choose a partner with native SAP BTP integration and proven S/4 migration experience</li>
<li>Plan for parallel deployment to avoid extending your critical path</li></ul>

<h2>About Taxera</h2><p>Taxera Technologies is a Swiss-headquartered tax technology company providing a unified compliance platform for global enterprises. Built natively on SAP BTP, Taxera covers e-Invoicing, eCompliance, eReporting, Tax Reporting, and Tax Operations across 80+ countries through a single platform with middleware that operates inside your SAP business process layer.</p>
<p><strong>Contact:</strong> <a href="mailto:info@taxera.tech" style="color:#0066CC">info@taxera.tech</a> | <a href="https://taxera.tech" style="color:#0066CC">taxera.tech</a></p>
<div class="footer">Copyright ${new Date().getFullYear()} Taxera Technologies GmbH. All rights reserved. This document is confidential.</div></div></body></html>`;

  const win = window.open('', '_blank');
  if (win) { const blob = new Blob([html], { type: 'text/html' }); win.location.href = URL.createObjectURL(blob); setTimeout(() => win.print(), 500); }
};

/* ═══════════════════════════════════════════════
   ASSESSMENT SECTION
   ═══════════════════════════════════════════════ */
const AssessmentSection = () => {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showLeadGate, setShowLeadGate] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [gateSource, setGateSource] = useState('assessment_report');

  const currentQ = ASSESSMENT_QUESTIONS[step];
  const progress = Object.keys(answers).length / ASSESSMENT_QUESTIONS.length;

  const handleAnswer = (score) => {
    const newAnswers = { ...answers, [currentQ.id]: score };
    setAnswers(newAnswers);
    if (step < ASSESSMENT_QUESTIONS.length - 1) {
      setTimeout(() => setStep(s => s + 1), 200);
    } else {
      setTimeout(() => setShowResults(true), 300);
    }
  };

  const scores = ASSESSMENT_QUESTIONS.map(q => answers[q.id] || 0);
  const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
  const overallPct = Math.round((avgScore / 4) * 100);

  const getGrade = (pct) => {
    if (pct >= 80) return { label: 'Excellent', color: '#10b981', desc: 'Your compliance infrastructure is well-positioned for S/4HANA. Focus on optimization and mandate coverage expansion.' };
    if (pct >= 60) return { label: 'Good', color: '#3b82f6', desc: 'Solid foundation, but gaps in automation and coverage could create risk during migration. Platform consolidation would accelerate your journey.' };
    if (pct >= 40) return { label: 'At Risk', color: '#f59e0b', desc: 'Significant gaps across multiple dimensions. Without intervention, your S/4 migration will face compliance disruptions. Urgent action recommended.' };
    return { label: 'Critical', color: '#ef4444', desc: 'Your current compliance infrastructure is not ready for S/4HANA. Multi-vendor fragmentation, manual processes, and limited coverage create material risk. Immediate platform investment required.' };
  };

  const handleDownload = (source) => {
    if (leadCaptured) {
      if (source === 'whitepaper') generateWhitepaper(leadCaptured);
      else generateAssessmentReport(scores, overallPct, getGrade(overallPct), leadCaptured);
    } else {
      setGateSource(source);
      setShowLeadGate(true);
    }
  };

  const handleLeadSuccess = (form) => {
    setLeadCaptured(form.name);
    setShowLeadGate(false);
    if (gateSource === 'whitepaper') generateWhitepaper(form.name);
    else generateAssessmentReport(scores, overallPct, getGrade(overallPct), form.name);
  };

  if (!started) {
    return (
      <div className="text-center">
        <h3 className="font-heading text-xl font-bold text-taxera-charcoal mb-3">How ready are you?</h3>
        <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">8 questions. 2 minutes. Get a personalized scorecard across Cloud, Coverage, Integration, Automation, and more.</p>
        <button
          onClick={() => setStarted(true)}
          className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105"
          data-testid="assessment-start-btn"
        >
          Start Assessment <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
        <p className="text-2xs text-slate-400 mt-3">Free for everyone. Detailed PDF report requires registration.</p>
      </div>
    );
  }

  if (showResults) {
    const grade = getGrade(overallPct);
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="assessment-results">
        <LeadGateModal
          open={showLeadGate}
          onClose={() => setShowLeadGate(false)}
          onSuccess={handleLeadSuccess}
          title={gateSource === 'whitepaper' ? 'Download Whitepaper' : 'Download Your Report'}
          subtitle={gateSource === 'whitepaper' ? 'Get the complete SAP S/4HANA tax compliance guide.' : 'Get your personalized readiness scorecard as a PDF.'}
          source={gateSource}
        />
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-2xs font-bold text-slate-400 uppercase tracking-[0.15em] mb-3">Your readiness score</div>
            <div className="flex items-baseline gap-3 mb-2">
              <span className="font-heading text-6xl font-bold" style={{ color: grade.color }}>{overallPct}%</span>
              <span className="text-lg font-bold" style={{ color: grade.color }}>{grade.label}</span>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-6">{grade.desc}</p>
            <div className="space-y-2 mb-6">
              {ASSESSMENT_QUESTIONS.map((q, i) => (
                <div key={q.id} className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 w-28 shrink-0 font-medium">{q.dimension}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${(scores[i] / 4) * 100}%`, backgroundColor: scores[i] >= 3 ? '#10b981' : scores[i] >= 2 ? '#f59e0b' : '#ef4444' }} />
                  </div>
                  <span className="text-xs font-bold text-slate-500 w-6 text-right">{scores[i]}/4</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="group inline-flex items-center gap-2 px-6 py-3 taxera-gradient text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all" data-testid="assessment-cta-expert">
                Discuss with an Expert <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <button onClick={() => handleDownload('assessment_report')} className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-full text-sm font-semibold hover:bg-slate-50 transition-colors" data-testid="assessment-download-btn">
                <Download className="w-3.5 h-3.5" /> Download PDF Report
              </button>
            </div>
          </div>
          <RadarChart scores={scores} dimensions={ASSESSMENT_QUESTIONS.map(q => q.dimension)} />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="assessment-question">
      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
          <span>Question {step + 1} of {ASSESSMENT_QUESTIONS.length}</span>
          <span className="font-semibold">{currentQ.dimension}</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <motion.div className="h-full bg-taxera-teal rounded-full" animate={{ width: `${progress * 100}%` }} />
        </div>
      </div>
      <h3 className="font-heading text-lg font-bold text-taxera-charcoal mb-5">{currentQ.question}</h3>
      <div className="space-y-2.5">
        {currentQ.options.map((opt, i) => (
          <button
            key={`opt-${opt.score}`}
            onClick={() => handleAnswer(opt.score)}
            className={`w-full text-left p-4 rounded-xl border transition-all hover:shadow-md ${
              answers[currentQ.id] === opt.score ? 'border-taxera-teal bg-taxera-teal/5' : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
            data-testid={`assessment-option-${i}`}
          >
            <span className="text-sm text-slate-700">{opt.label}</span>
          </button>
        ))}
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════
   TCO CALCULATOR
   ═══════════════════════════════════════════════ */
const TcoCalculator = () => {
  const [countries, setCountries] = useState(15);
  const [vendors, setVendors] = useState(5);
  const [annualSpend, setAnnualSpend] = useState(500);

  const currentTCO = useMemo(() => {
    const vendorCost = vendors * 38000;
    const integrationOverhead = vendors * 15000;
    const internalLabor = countries * 6500;
    return annualSpend * 1000 + vendorCost + integrationOverhead + internalLabor;
  }, [countries, vendors, annualSpend]);

  const taxeraTCO = useMemo(() => {
    const platformFee = 85000 + countries * 4200;
    const internalLabor = countries * 1800;
    return platformFee + internalLabor;
  }, [countries]);

  const savings = currentTCO - taxeraTCO;
  const savingsPct = currentTCO > 0 ? Math.round((savings / currentTCO) * 100) : 0;

  const fmt = (v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : `${Math.round(v / 1000)}K`;

  return (
    <div data-testid="tco-calculator">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="space-y-6">
          <div>
            <label className="flex items-center justify-between text-sm font-semibold text-taxera-charcoal mb-2">
              Countries requiring compliance <span className="text-taxera-teal font-bold">{countries}</span>
            </label>
            <input type="range" min={1} max={50} value={countries} onChange={e => setCountries(+e.target.value)} className="w-full accent-taxera-teal" data-testid="tco-countries-slider" />
            <div className="flex justify-between text-2xs text-slate-400 mt-1"><span>1</span><span>50</span></div>
          </div>
          <div>
            <label className="flex items-center justify-between text-sm font-semibold text-taxera-charcoal mb-2">
              Current compliance vendors <span className="text-taxera-teal font-bold">{vendors}</span>
            </label>
            <input type="range" min={1} max={15} value={vendors} onChange={e => setVendors(+e.target.value)} className="w-full accent-taxera-teal" data-testid="tco-vendors-slider" />
            <div className="flex justify-between text-2xs text-slate-400 mt-1"><span>1</span><span>15</span></div>
          </div>
          <div>
            <label className="flex items-center justify-between text-sm font-semibold text-taxera-charcoal mb-2">
              Annual compliance spend (K EUR) <span className="text-taxera-teal font-bold">{annualSpend}K</span>
            </label>
            <input type="range" min={50} max={3000} step={50} value={annualSpend} onChange={e => setAnnualSpend(+e.target.value)} className="w-full accent-taxera-teal" data-testid="tco-spend-slider" />
            <div className="flex justify-between text-2xs text-slate-400 mt-1"><span>50K</span><span>3M</span></div>
          </div>
        </div>
        {/* Results */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-2">Estimated current TCO</div>
            <div className="font-heading text-3xl font-bold text-slate-400 line-through">EUR {fmt(currentTCO)}</div>
            <p className="text-xs text-slate-400 mt-1">Includes vendor fees, integration overhead, and internal labor</p>
          </div>
          <div className="bg-taxera-teal/5 rounded-2xl border border-taxera-teal/20 p-5">
            <div className="text-2xs font-bold text-taxera-teal uppercase tracking-wide mb-2">Projected TCO with Taxera</div>
            <div className="font-heading text-3xl font-bold text-taxera-teal">EUR {fmt(taxeraTCO)}</div>
            <p className="text-xs text-slate-500 mt-1">Single platform fee + reduced internal coordination</p>
          </div>
          <div className="bg-slate-900 rounded-2xl p-5 text-center">
            <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-1">Estimated annual savings</div>
            <div className="font-heading text-4xl font-bold text-white">EUR {fmt(Math.max(0, savings))}</div>
            <div className="text-lg font-bold text-taxera-teal">{Math.max(0, savingsPct)}% reduction</div>
          </div>
          <p className="text-2xs text-slate-400 text-center">Illustrative model based on industry benchmarks. Actual savings depend on specific configuration.</p>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════ */
const SapMigrationPage = () => {
  const [activePersona, setActivePersona] = useState(0);
  const [expandedPhase, setExpandedPhase] = useState(null);

  return (
    <PageLayout>
      <SapSubNav />
      {/* ─── Hero ─── */}
      <section className="pb-16 pt-8" data-testid="sap-hero">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-blue-600 text-sm font-medium rounded-full mb-6">
                <Server className="w-4 h-4" strokeWidth={1.5} />
                SAP S/4HANA Migrations
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-taxera-charcoal leading-[1.1] mb-5">
                Don't let tax compliance <span className="taxera-gradient-text">derail your S/4 migration.</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-8">
                37,000+ enterprises are migrating to SAP S/4HANA. Most will discover — too late — that their tax compliance infrastructure doesn't survive the move.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105" data-testid="sap-cta-primary">
                  Talk to an SAP Expert <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href="/sap-s4hana/assessment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-taxera-charcoal border border-slate-200 rounded-full font-semibold hover:bg-slate-50 transition-colors" data-testid="sap-cta-assess">
                  Take the Readiness Assessment
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="grid grid-cols-2 gap-3">
              {STATS.map((stat, i) => (
                <div key={stat.label} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid={`sap-stat-${i}`}>
                  <div className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal mb-1">{stat.value}</div>
                  <div className="text-xs text-slate-500 font-medium">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Program Journey ─── */}
      <section id="journey" className="scroll-mt-[9rem] py-16 bg-slate-50" data-testid="sap-journey">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal mb-3">The S/4HANA program journey</h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">Taxera integrates into every phase of your migration — not as a bolt-on, but as a core workstream.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PROGRAM_PHASES.map((p, i) => (
              <motion.button
                key={p.phase}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                onClick={() => setExpandedPhase(expandedPhase === i ? null : i)}
                className={`text-left rounded-2xl border p-5 transition-all ${expandedPhase === i ? 'bg-slate-900 border-slate-700 shadow-lg' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'}`}
                data-testid={`sap-phase-${i}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: p.color }}>{i + 1}</div>
                  <h3 className={`font-heading text-base font-bold ${expandedPhase === i ? 'text-white' : 'text-taxera-charcoal'}`}>{p.phase}</h3>
                </div>
                <p className={`text-xs mb-2 ${expandedPhase === i ? 'text-slate-400' : 'text-slate-500'}`}>{p.desc}</p>
                <AnimatePresence>
                  {expandedPhase === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="pt-3 border-t border-white/10">
                        <div className="text-2xs font-bold text-taxera-teal uppercase tracking-wide mb-1">Taxera's role</div>
                        <p className="text-xs text-slate-300 leading-relaxed">{p.taxera}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Migration Timeline ─── */}
      <section id="timeline" className="scroll-mt-[9rem] py-16" data-testid="sap-timeline">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal mb-3">The clock is ticking</h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">SAP ECC mainstream support ends December 2027.</p>
          </div>
          <div className="space-y-4">
            {TIMELINE.map((item, i) => (
              <motion.div key={item.year} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex items-start gap-5">
                <div className={`shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center font-heading text-lg font-bold ${item.status === 'now' ? 'bg-taxera-blue text-white' : item.status === 'upcoming' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500'}`}>
                  {item.year}
                </div>
                <div className="flex-1 bg-white rounded-2xl border border-slate-200 p-5">
                  <p className="text-sm text-slate-700 leading-relaxed">{item.event}</p>
                  {item.status === 'now' && <span className="inline-flex items-center gap-1 mt-2 text-2xs font-bold text-taxera-blue uppercase tracking-wide"><span className="w-1.5 h-1.5 rounded-full bg-taxera-blue animate-pulse" /> Happening now</span>}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Market Landscape ─── */}
      <section id="landscape" className="scroll-mt-[9rem] py-20 bg-slate-900" data-testid="sap-landscape">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-3">Not all compliance platforms are equal</h2>
            <p className="text-base text-slate-400">The market is full of options. But the architecture behind the label matters more than the label itself.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-12">
            {MARKET_APPROACHES.map((ma, i) => (
              <div key={ma.approach} className="bg-white/5 rounded-2xl border border-white/10 p-6" data-testid={`market-approach-${i}`}>
                <h3 className="font-heading text-base font-bold text-white mb-3">{ma.approach}</h3>
                <ul className="space-y-1.5 mb-4">
                  {ma.traits.map((t, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs text-slate-400"><Minus className="w-3 h-3 text-slate-600 shrink-0 mt-0.5" /> {t}</li>
                  ))}
                </ul>
                <p className="text-xs text-amber-400/80 leading-relaxed border-t border-white/10 pt-3">{ma.limitation}</p>
              </div>
            ))}
          </div>
          {/* Taxera differentiators */}
          <div className="bg-taxera-teal/10 rounded-2xl border border-taxera-teal/20 p-8">
            <h3 className="font-heading text-lg font-bold text-white mb-2">The Taxera approach: Born-cloud. Single platform. Global from day one.</h3>
            <p className="text-sm text-slate-400 mb-6">Not acquired. Not bolted-on. Not limited. Built from the ground up as one platform for every mandate, every country, every stakeholder.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {TAXERA_DIFFERENTIATORS.map((d, i) => {
                const Icon = d.icon;
                return (
                  <div key={`diff-${d.label}`} className="flex items-start gap-3">
                    <Icon className="w-4 h-4 text-taxera-teal shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <div className="text-xs font-bold text-white">{d.label}</div>
                      <div className="text-2xs text-slate-400 leading-relaxed">{d.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Persona Perspectives ─── */}
      <section id="personas" className="scroll-mt-[9rem] py-20" data-testid="sap-personas">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-10">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal mb-3">Every stakeholder, one answer</h2>
            <p className="text-base text-slate-500">Whether you drive the migration from Tax, IT, Finance, or the PMO — Taxera addresses your specific concerns.</p>
          </div>
          <div className="flex flex-wrap gap-2 mb-8">
            {PERSONAS.map((p, i) => {
              const Icon = p.icon;
              return (
                <button key={p.role} onClick={() => setActivePersona(i)} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${activePersona === i ? 'text-white shadow-lg' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}`} style={activePersona === i ? { backgroundColor: p.color } : {}} data-testid={`sap-persona-tab-${i}`}>
                  <Icon className="w-4 h-4" strokeWidth={1.5} /> {p.role}
                </button>
              );
            })}
          </div>
          <motion.div key={activePersona} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="text-2xs font-bold text-slate-400 uppercase tracking-[0.15em] mb-4">Key concerns</div>
              <div className="space-y-3">
                {PERSONAS[activePersona].concerns.map((c, i) => (
                  <div key={`concern-${i}`} className="flex items-start gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">{i + 1}</span>
                    <p className="text-sm text-slate-700 leading-relaxed">{c}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl p-6" style={{ backgroundColor: `${PERSONAS[activePersona].color}08`, borderColor: `${PERSONAS[activePersona].color}20`, borderWidth: 1 }}>
              <div className="text-2xs font-bold uppercase tracking-[0.15em] mb-4" style={{ color: PERSONAS[activePersona].color }}>Taxera's answer</div>
              <p className="text-sm text-slate-700 leading-relaxed">{PERSONAS[activePersona].answer}</p>
              <Link to="/contact" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold transition-colors hover:gap-3" style={{ color: PERSONAS[activePersona].color }} data-testid={`sap-persona-cta-${activePersona}`}>
                Discuss with our team <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Assessment & Calculator CTAs ─── */}
      <section className="py-20 bg-slate-50" data-testid="sap-tools">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal mb-3">Interactive tools for your migration</h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">Evaluate your readiness, model your savings, and download the definitive guide.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <Link to="/sap-s4hana/assessment" className="group bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-all hover:border-taxera-teal/30" data-testid="sap-tool-assessment">
              <div className="w-10 h-10 rounded-xl bg-taxera-teal/10 flex items-center justify-center mb-4">
                <BarChart3 className="w-5 h-5 text-taxera-teal" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-2">Readiness Assessment</h3>
              <p className="text-sm text-slate-500 mb-4">8 questions. 2 minutes. Get a personalized scorecard with radar chart and downloadable PDF report.</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-taxera-teal group-hover:gap-2 transition-all">
                Start Assessment <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link to="/sap-s4hana/calculator" className="group bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-all hover:border-blue-200" data-testid="sap-tool-calculator">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                <DollarSign className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-2">TCO Calculator</h3>
              <p className="text-sm text-slate-500 mb-4">Configure your compliance footprint and see projected savings with platform consolidation.</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all">
                Calculate Savings <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link to="/sap-s4hana/whitepaper" className="group bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-all hover:border-slate-300" data-testid="sap-tool-whitepaper">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                <FileText className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-2">Whitepaper</h3>
              <p className="text-sm text-slate-500 mb-4">The definitive guide for Tax Directors, CIOs, and CFOs navigating ERP transformation and compliance.</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 group-hover:gap-2 transition-all">
                Download Guide <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Use Cases ─── */}
      <section id="use-cases" className="scroll-mt-[9rem] py-20 bg-slate-50" data-testid="sap-use-cases">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-10">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal mb-3">Proven in production</h2>
            <p className="text-base text-slate-500">Real program outcomes from enterprise S/4HANA migrations.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {USE_CASES.map((uc, i) => (
              <motion.div
                key={uc.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col"
                data-testid={`use-case-${i}`}
              >
                <div className="text-2xs font-bold text-taxera-teal uppercase tracking-wide mb-3">{uc.industry}</div>
                <div className="mb-3">
                  <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-1">Challenge</div>
                  <p className="text-sm text-slate-600 leading-relaxed">{uc.challenge}</p>
                </div>
                <div className="mb-4 flex-1">
                  <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-1">Outcome</div>
                  <p className="text-sm text-slate-600 leading-relaxed">{uc.outcome}</p>
                </div>
                <div className="flex gap-3 pt-3 border-t border-slate-100">
                  {uc.metrics.map((m, j) => (
                    <div key={`metric-${m.label}`} className="text-center flex-1">
                      <div className="font-heading text-lg font-bold text-taxera-charcoal">{m.value}</div>
                      <div className="text-2xs text-slate-400 font-medium">{m.label}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Compliance Challenge Game ─── */}
      <section className="py-20" data-testid="sap-game-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal mb-3">Test your compliance knowledge</h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">Think you understand the risks of S/4HANA migration? Put your knowledge to the test with real-world scenarios.</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
            <SapGameChallenge />
          </div>
        </div>
      </section>

      {/* ─── Blog Cross-References ─── */}
      <section className="py-16" data-testid="sap-blog-refs">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-bold text-taxera-charcoal">Related reading</h2>
            <Link to="/blog" className="text-sm text-taxera-blue font-semibold hover:underline flex items-center gap-1">All articles <ArrowUpRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {BLOG_REFS.map((ref) => (
              <Link key={ref.slug} to={`/blog/${ref.slug}`} className="group bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:border-slate-300 transition-all" data-testid={`blog-ref-${ref.slug}`}>
                <BookOpen className="w-5 h-5 text-slate-400 mb-3 group-hover:text-taxera-blue transition-colors" strokeWidth={1.5} />
                <h3 className="text-sm font-bold text-taxera-charcoal group-hover:text-taxera-blue transition-colors leading-snug">{ref.title}</h3>
                <span className="inline-flex items-center gap-1 text-2xs text-taxera-blue font-semibold mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Read article <ArrowRight className="w-3 h-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-20 bg-slate-900" data-testid="sap-cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">
            Your S/4 migration is complex enough.<br />Tax compliance doesn't have to be.
          </h2>
          <p className="text-base text-slate-400 mb-8 max-w-xl mx-auto">
            Talk to one of our SAP compliance specialists. We'll show you exactly how Taxera fits into your migration project — and what the alternative costs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-taxera-teal text-white rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105" data-testid="sap-cta-bottom">
              Schedule an SAP Expert Call <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href="#assessment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white border border-white/20 rounded-full font-semibold hover:bg-white/15 transition-colors" data-testid="sap-cta-assess-bottom">
              Take the Assessment
            </a>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default SapMigrationPage;
